import { FC, useState, MouseEvent, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { TokenInclude } from "helpers/GetToken";
import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { Panel, PanelHeader, Typography } from "components";
import { StyledFormWrapper, Row, Col } from "components/BaseComponents/Flex";
import Image, { ICONS } from "components/BaseComponents/Image";
import {
  StyledProfileName,
  StyledProfileWrapper,
  StyledProfileNameTag,
  StyledProfileBox,
  StyledProfileCircleWrapper,
  StyledProfileIconCircle,
  StyledProfileBoxImage,
  StyledMobileWrapper,
} from "./ProfileInfo.styles";
import {
  formattingUserName,
  initialsFromName,
  maxName,
  maxUserName,
} from "utils/textfield.utils";
import SupportModal from "./SupportModal";
import { employeePhotoAsync } from "redux/actions/EmployeePhoto.action";

const ProfileInfo: FC = () => {
  const { merchantName } = useParams<string>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.login);
  const { userOtp } = useAppSelector((state) => state.otp);
  const token = TokenInclude();

  const userName: string = user?.adSoyad!;

  const { employeePhoto } = useAppSelector((state) => state.employeePhoto);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const openSupportModal = (e: MouseEvent) => {
    e.stopPropagation();
    setOpenModal((prev) => !prev);
  };

  useEffect(() => {
    if (!employeePhoto?.Avatar) {
      dispatch(employeePhotoAsync(token));
    }
  }, [employeePhoto?.Avatar]);

  return (
    <Panel width='872px' marginTop='0px' marginBottom='0px'>
      <StyledMobileWrapper>
        <StyledProfileWrapper>
          {employeePhoto?.Avatar ? (
            <Image
              alt='Profile Photo'
              srcSet={
                employeePhoto?.Avatar
                  ? `data:image/png;base64, ${employeePhoto?.Avatar}`
                  : undefined
              }
              height='120px'
              width='120px'
              borderRadius='50%'
              border='1px solid #f5f4f6'
              margin='15px 0'
              padding='0'
            />
          ) : (
            <StyledProfileNameTag>
              {maxName(formattingUserName(initialsFromName(userName)))}
            </StyledProfileNameTag>
          )}
        </StyledProfileWrapper>
        <PanelHeader />
        <StyledFormWrapper>
          <StyledProfileName>
            <Typography
              width=''
              size={isMobile ? "16px" : "18px"}
              margin='25px 0 0 0'
            >
              {userName}
            </Typography>
          </StyledProfileName>
          <Row
            padding='20px 0 0 0'
            backgroundColor={DIGIPOSTHEME.colors.transparent}
          >
            <Col isDisplayFlex={true} justifyContent='flex-end'>
              <StyledProfileBox>
                <StyledProfileBoxImage>
                  <Image
                    alt='Menu Icon'
                    height={isMobile ? "20px" : "24px"}
                    srcSet={ICONS.STAR}
                    width={isMobile ? "20px" : "24px"}
                  />
                </StyledProfileBoxImage>
                <Typography width='' size={isMobile ? "12px" : "14px"}>
                  {t(
                    userOtp?.UserLevel === "1" || userOtp?.UserLevel === "3"
                      ? "marchant_owner"
                      : "Agent"
                  )}
                </Typography>
              </StyledProfileBox>
            </Col>
            <Col isDisplayFlex={true} justifyContent='flex-start'>
              <StyledProfileBox>
                <Typography
                  width='200px'
                  size={isMobile ? "12px" : "14px"}
                  height='30px'
                  padding='0 5px'
                >
                  {maxUserName(merchantName)}
                </Typography>
              </StyledProfileBox>
            </Col>
          </Row>
          <Row
            padding={isMobile ? "20px 0 0 0" : "40px 0 0 0"}
            backgroundColor={DIGIPOSTHEME.colors.transparent}
          >
            {(userOtp?.UserLevel === "1" || userOtp?.UserLevel === "3") && (
              <>
                <Col isDisplayFlex={true} justifyContent='center'>
                  <StyledProfileCircleWrapper
                    onClick={() => navigate("/contact-info")}
                  >
                    <StyledProfileIconCircle>
                      <Image
                        alt='Menu Icon'
                        height={isMobile ? "24px" : "30px"}
                        srcSet={ICONS.PROFILE}
                        width={isMobile ? "24px" : "30px"}
                      />
                    </StyledProfileIconCircle>
                    <Typography width='' size={isMobile ? "13px" : "14px"}>
                      {t("contact_info")}
                    </Typography>
                  </StyledProfileCircleWrapper>
                </Col>
                <Col isDisplayFlex={true} justifyContent='center'>
                  <StyledProfileCircleWrapper
                    onClick={() => navigate("/working-conditions")}
                  >
                    <StyledProfileIconCircle>
                      <Image
                        alt='Menu Icon'
                        height={isMobile ? "18px" : "20px"}
                        srcSet={ICONS.TXT}
                        width={isMobile ? "18px" : "20px"}
                      />
                    </StyledProfileIconCircle>
                    <Typography width='' size={isMobile ? "13px" : "14px"}>
                      {t("Working_conditions")}
                    </Typography>
                  </StyledProfileCircleWrapper>
                </Col>
              </>
            )}
            <Col isDisplayFlex={true} justifyContent='center'>
              <StyledProfileCircleWrapper onClick={(e) => openSupportModal(e)}>
                <StyledProfileIconCircle>
                  <Image
                    alt='Menu Icon'
                    height={isMobile ? "18px" : "20px"}
                    srcSet={ICONS.SUPPORT}
                    width={isMobile ? "18px" : "20px"}
                  />
                </StyledProfileIconCircle>
                <Typography width='' size={isMobile ? "13px" : "14px"}>
                  {t("support")}
                </Typography>
              </StyledProfileCircleWrapper>
            </Col>
          </Row>
        </StyledFormWrapper>
      </StyledMobileWrapper>
      {openModal && (
        <SupportModal
          rejectButtonText={t("modal_error_button_title").toString()}
          message={t("support_warning").toString()}
          setIsOpen={setOpenModal}
          title={t("information").toString()}
        />
      )}
    </Panel>
  );
};

export default ProfileInfo;
